import {
  Text,
  Tree,
  TreeItem,
  TreeItemLayout,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import type { ReactElement } from 'react';
import { folderTree, type FolderNode } from '../state/folders.js';
import { useController, useMail } from '../state/context.js';

const useStyles = makeStyles({
  root: { padding: '4px 8px 12px' },
  layout: { borderRadius: tokens.borderRadiusMedium },
  active: {
    background: tokens.colorBrandBackground2,
    fontWeight: tokens.fontWeightSemibold,
  },
  unread: { fontWeight: tokens.fontWeightBold },
  count: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
});

export function FolderTree(): ReactElement {
  const styles = useStyles();
  const folders = useMail((s) => s.folders);
  const nodes = folderTree(folders);

  return (
    <nav className={styles.root} aria-label="Folders">
      <Tree aria-label="Folders" defaultOpenItems={folders.map((f) => f.id)} size="small">
        {nodes.map((node) => (
          <FolderBranch key={node.folder.id} node={node} />
        ))}
      </Tree>
    </nav>
  );
}

function FolderBranch({ node }: { node: FolderNode }): ReactElement {
  const styles = useStyles();
  const controller = useController();
  const activeFolderId = useMail((s) => s.activeFolderId);
  const { folder, children } = node;
  const active = folder.id === activeFolderId;

  return (
    <TreeItem
      value={folder.id}
      itemType={children.length === 0 ? 'leaf' : 'branch'}
      data-folder-role={folder.role}
      aria-label={
        folder.unreadCount > 0 ? `${folder.name}, ${folder.unreadCount} unread` : folder.name
      }
      aria-current={active ? 'true' : undefined}
      onClick={() => void controller.selectFolder(folder.id)}
    >
      <TreeItemLayout
        className={mergeClasses(styles.layout, active ? styles.active : undefined)}
        aside={
          folder.unreadCount > 0 ? (
            <Text className={styles.count} aria-label={`${folder.unreadCount} unread`}>
              {folder.unreadCount}
            </Text>
          ) : null
        }
      >
        <Text className={folder.unreadCount > 0 ? styles.unread : undefined}>{folder.name}</Text>
      </TreeItemLayout>
      {children.length > 0 && (
        <Tree onClick={(event) => event.stopPropagation()}>
          {children.map((child) => (
            <FolderBranch key={child.folder.id} node={child} />
          ))}
        </Tree>
      )}
    </TreeItem>
  );
}
